import { Component, Vue, Prop } from 'vue-property-decorator';
import { EVALUATION_STATUSES, MENU_ACTIONS, NOTIFICATION, PAGE_NAMES } from '@/constants';
import { convertNumberToFourDigitsString } from '@/helpers';
import { ActionMenuItem, AssessmentPeriodDTO, DisplayStatus, ProjectDTO, ProjectManager, UserDTO } from '@/models';
import { deleteProject, stopEvaluationProject, updateProjectArchiveState, updateProjectManagers } from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import Status from '@/components/common/status/Status.vue';
import ActionMenu from '@/components/common/action-menu/ActionMenu.vue';
import PageLoader from '@/components/common/loader/page-loader/PageLoader.vue';
import LinkHRMProjectDialog from './link-HRM-project-dialog/LinkHRMProjectDialog.vue';
import ProjectExportModal from './project-export-modal/ProjectExportModal.vue';
import ProjectManagerEditDialog from './project-manager-edit-dialog/ProjectManagerEditDialog.vue';
import { Getter } from '@/decorators';

@Component({
  components: {
    ActionMenu,
    ConfirmationDialog,
    PageLoader,
    Status,
    LinkHRMProjectDialog,
    ProjectExportModal,
    ProjectManagerEditDialog
  }
})
export default class ProjectEvaluationHeader extends Vue {
  @Getter('project/managers') projectManager: ProjectManager[];
  @Getter('project/projectAssessmentId') projectAssessmentId: number;

  @Prop() projectInfo: ProjectDTO;
  @Prop() currentUser: UserDTO;
  @Prop({ default: '' }) pathTitle: UserDTO;
  @Prop({ default: () => [] }) assessmentPeriods: AssessmentPeriodDTO[];

  private isCancelDialogShown = false;
  private isEvaluationDialogStopped = false;
  private linkHRMProjectDialogShown = false;
  private modalShown = false;

  projectManagerEditDialogShown = false;

  navigateToProjectListPage(): void {
    this.$router.push({ name: PAGE_NAMES.PROJECT_LIST });
  }

  navigateToProjectEvaluation(): void {
    const { name, params } = this.$route;
    const nextPageName = PAGE_NAMES.PROJECT_EVALUATION;
    const nextPageParams = {
      id: '' + this.projectInfo.id
    };

    if (name !== nextPageName && params.id !== nextPageParams.id) {
      this.$router.push({
        name: nextPageName,
        params: nextPageParams
      });
    }
  }

  async deleteProject(): Promise<void> {
    await deleteProject(this.projectInfo.id);
    this.isCancelDialogShown = false;
    await this.$router.push({ name: PAGE_NAMES.PROJECT_LIST });
  }

  async stopEvaluationProject(): Promise<void> {
    await stopEvaluationProject(this.projectInfo.id);
    await this.$router.push({ name: PAGE_NAMES.PROJECT_LIST });
  }

  handleEditProject(): void {
    this.projectManagerEditDialogShown = true;
  }

  get isProjectAdmin(): boolean {
    return this.currentUser.id === this.projectInfo.sponsor.id || this.currentUser.id === this.projectInfo.manager.id;
  }

  get menuItems(): ActionMenuItem[] {
    const {
      currentUser,
      projectInfo,
      openLinkHRMProjectDialog,
      openStopEvaluationDialog,
      archiveProject,
      isProjectAdmin,
      exportProject,
      openDeleteProjectDialog,
      handleEditProject
    } = this;
    const { isArchived, projectStatus } = projectInfo;
    const { isAdmin } = currentUser;

    const { LINK_HRM_PROJECT, EXPORT, DELETE, STOP_EVALUATION, ARCHIVE, UNARCHIVE, EDIT_MANAGER } = MENU_ACTIONS;
    const linkHRMProjectItem = {
      label: LINK_HRM_PROJECT,
      icon: 'mdi-link',
      handler: openLinkHRMProjectDialog
    };
    const exportItem = { label: EXPORT, icon: 'mdi-file-table', handler: exportProject };
    const editItem = { label: EDIT_MANAGER, icon: 'mdi-pencil', handler: handleEditProject };
    const deleteItem = { label: DELETE, icon: 'mdi-delete', handler: openDeleteProjectDialog };
    const stopEvaluationItem = {
      label: STOP_EVALUATION,
      icon: 'mdi-clipboard-remove',
      handler: openStopEvaluationDialog
    };
    const archiveItem = {
      label: isArchived ? UNARCHIVE : ARCHIVE,
      icon: isArchived ? 'mdi-archive-off' : 'mdi-archive',
      handler: archiveProject
    };
    const menuItems: ActionMenuItem[] = [];
    if (isAdmin || isProjectAdmin) {
      menuItems.push(exportItem);
      menuItems.push(editItem);
    }
    if (isAdmin) {
      menuItems.push(archiveItem);
      menuItems.push(deleteItem);
      menuItems.unshift(linkHRMProjectItem);
    }
    if (isAdmin && projectStatus === EVALUATION_STATUSES.ON_GOING.label) {
      menuItems.push(stopEvaluationItem);
    }

    return menuItems;
  }

  get displayID(): string {
    return convertNumberToFourDigitsString(this.projectInfo?.id);
  }

  get displayStatus(): DisplayStatus {
    return EVALUATION_STATUSES[this.projectInfo.projectStatus];
  }

  exportProject(): void {
    this.modalShown = true;
  }

  openDeleteProjectDialog(): void {
    this.isCancelDialogShown = true;
  }

  openStopEvaluationDialog(): void {
    this.isEvaluationDialogStopped = true;
  }

  openLinkHRMProjectDialog(): void {
    this.linkHRMProjectDialogShown = true;
  }

  async archiveProject(): Promise<void> {
    const { id, isArchived } = this.projectInfo;
    await updateProjectArchiveState(id, !isArchived);
    await this.$router.push({ name: PAGE_NAMES.PROJECT_LIST });
  }

  async updateManagers(managers: UserDTO[]) {
    try {
      await updateProjectManagers(
        this.projectInfo?.id,
        this.projectAssessmentId,
        managers.map(manager => Number(manager.id))
      );
      const updateManagersSuccess = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.updateManagersSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false
      };
      this.$store.commit('notification/setNotification', updateManagersSuccess);
      const updatedProjectInfo: ProjectDTO = {
        ...this.projectInfo,
        coManagers: managers
      };
      this.$store.commit('project/setProject', updatedProjectInfo);
    } catch (error) {
      const updateManagersFailed = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: NOTIFICATION.TEXT.updateManagersFailed,
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false
      };
      this.$store.commit('notification/setNotification', updateManagersFailed);
    }
  }
}
